import {
	DEFAULT_SORT_FIELD,
	decodeFilters,
	encodeFilters,
	filtersEqual,
	getEventTargetElement,
	SORT_FIELD_GROUP,
} from './Util';

class DySearchFilterController {
	static desktopCloseDelay = 250;

	static getFilterPayload( filters ) {
		const payload = { ...filters };
		delete payload[ SORT_FIELD_GROUP ];

		return payload;
	}

	static getCleanLabel( labelEl ) {
		return String( labelEl?.textContent || '' )
			.replace( /\s*\(\d+\)\s*$/u, '' )
			.replace( /\s+/g, ' ' )
			.trim();
	}

	static syncPriceSliderAfterProgrammaticChange( sliderEl ) {
		if ( ! sliderEl ) {
			return;
		}

		sliderEl.querySelector( '.controls input[type="range"]' )?.dispatchEvent( new Event( 'input', { bubbles: true } ) );
	}

	static isNewArrivedGroup( group ) {
		return [ 'newarrived', 'newarriveds', 'NewArrived' ].includes( String( group || '' ) );
	}

	constructor( formEl ) {
		this.formEl = formEl;
		this.init();
	}

	parseInitialFilters() {
		try {
			return JSON.parse( this.formEl.dataset.initialFilters || '{}' );
		} catch ( error ) {
			return {};
		}
	}

	getInitialSortField() {
		return String( this.formEl.dataset.initialSortField || DEFAULT_SORT_FIELD );
	}

	getComparableInitialFilters() {
		const filters = this.parseInitialFilters();
		const initialSortField = this.getInitialSortField();

		if ( initialSortField !== DEFAULT_SORT_FIELD ) {
			filters[ SORT_FIELD_GROUP ] = initialSortField;
		}
		this.normalizeNewArrivedFilters( filters );

		return filters;
	}

	syncSortFieldInput( filters ) {
		let sortInput = this.formEl.querySelector( 'input[name="sortField"]' );
		const sortField = filters[ SORT_FIELD_GROUP ] || DEFAULT_SORT_FIELD;

		if ( sortField === DEFAULT_SORT_FIELD ) {
			sortInput?.remove();
			return;
		}

		if ( ! sortInput ) {
			sortInput = document.createElement( 'input' );
			sortInput.type = 'hidden';
			sortInput.name = 'sortField';
			this.formEl.appendChild( sortInput );
		}

		sortInput.value = sortField;
	}

	getPendingCategoryFilters() {
		try {
			return JSON.parse( this.formEl.dataset.categoryFilters || '{}' );
		} catch ( error ) {
			return {};
		}
	}

	hasInitialCategoryFilters() {
		const initialFilters = this.parseInitialFilters();
		return Boolean( initialFilters.categories || initialFilters.categories_no_tmp );
	}

	collectFilters() {
		const initialFilters = this.parseInitialFilters();
		const pendingCategoryFilters = this.getPendingCategoryFilters();
		const filters = {};

		if ( this.formEl.dataset.resetCategory !== 'true' && Object.keys( pendingCategoryFilters ).length > 0 ) {
			if ( pendingCategoryFilters.categories ) {
				filters.categories = pendingCategoryFilters.categories;
			}

			if ( pendingCategoryFilters.categories_no_tmp ) {
				filters.categories_no_tmp = pendingCategoryFilters.categories_no_tmp;
			}
		} else if ( this.formEl.dataset.resetCategory !== 'true' ) {
			if ( initialFilters.categories ) {
				filters.categories = Array.isArray( initialFilters.categories )
					? [ ...initialFilters.categories ]
					: initialFilters.categories;
			}

			if ( initialFilters.categories_no_tmp ) {
				filters.categories_no_tmp = Array.isArray( initialFilters.categories_no_tmp )
					? [ ...initialFilters.categories_no_tmp ]
					: initialFilters.categories_no_tmp;
			}
		}

		this.formEl.querySelectorAll( '.dy_filter_input' ).forEach( ( inputEl ) => {
			if ( inputEl.type === 'range' ) {
				return;
			}

			if ( ! inputEl.checked ) {
				return;
			}

			const group = String( inputEl.dataset.group || '' );
			const value = String( inputEl.value || '' );

			if ( ! group || ! value ) {
				return;
			}

			if ( group === SORT_FIELD_GROUP ) {
				if ( value !== DEFAULT_SORT_FIELD ) {
					filters[ SORT_FIELD_GROUP ] = value;
				}
				return;
			}

			filters[ group ] ??= [];
			if ( ! filters[ group ].includes( value ) ) {
				filters[ group ].push( value );
			}
		} );

		Object.keys( filters ).forEach( ( key ) => {
			if ( Array.isArray( filters[ key ] ) && filters[ key ].every( ( value ) => typeof value !== 'object' ) ) {
				filters[ key ].sort();
			}
		} );
		this.normalizeNewArrivedFilters( filters );

		this.formEl.querySelectorAll( '.mdl_price_slider' ).forEach( ( sliderEl ) => {
			const minInput = sliderEl.querySelector( '[data-price-min]' );
			const maxInput = sliderEl.querySelector( '[data-price-max]' );
			const fromInput = sliderEl.querySelector( 'input[type="hidden"][data-price-from]' );
			const toInput = sliderEl.querySelector( 'input[type="hidden"][data-price-to]' );

			if ( ! minInput || ! maxInput || ! fromInput || ! toInput ) {
				return;
			}

			const min = Number( minInput.value );
			const max = Number( maxInput.value );
			const from = Number( fromInput.value );
			const to = Number( toInput.value );

			if ( Number.isNaN( min ) || Number.isNaN( max ) || Number.isNaN( from ) || Number.isNaN( to ) ) {
				return;
			}

			if ( from !== min || to !== max ) {
				filters.price = { from, to };
			} else {
				delete filters.price;
			}
		} );

		return filters;
	}

	normalizeNewArrivedFilters( filters ) {
		const hasNewToggle = Array.isArray( filters.new ) && filters.new.includes( '1' );
		const newArrivedKeys = Object.keys( filters ).filter( key => DySearchFilterController.isNewArrivedGroup( key ) );
		const hasNewArrivedFilter = newArrivedKeys.some( key => Array.isArray( filters[ key ] ) && filters[ key ].length > 0 );

		if ( hasNewToggle ) {
			newArrivedKeys.forEach( key => {
				delete filters[ key ];
			} );
		} else if ( hasNewArrivedFilter ) {
			delete filters.new;
		}

		return filters;
	}

	syncNewArrivedExclusivity( changedInputEl = null ) {
		const newToggleInputEls = Array.from( this.formEl.querySelectorAll( '.dy_filter_input[type="checkbox"][data-group="new"]' ) );
		const newArrivedInputEls = Array.from( this.formEl.querySelectorAll( '.dy_filter_input[type="radio"]' ) )
			.filter( inputEl => DySearchFilterController.isNewArrivedGroup( inputEl.dataset.group ) );

		if ( newToggleInputEls.length === 0 || newArrivedInputEls.length === 0 ) {
			return;
		}

		const changedNewArrived = changedInputEl?.type === 'radio' && DySearchFilterController.isNewArrivedGroup( changedInputEl.dataset.group );
		if ( changedNewArrived && changedInputEl.checked ) {
			newToggleInputEls.forEach( inputEl => {
				inputEl.checked = false;
			} );
		}

		const hasNewToggle = newToggleInputEls.some( inputEl => inputEl.checked );
		newArrivedInputEls.forEach( inputEl => {
			if ( hasNewToggle ) {
				inputEl.checked = false;
			}
			inputEl.disabled = hasNewToggle;
		} );
	}

	closeItem( itemEl, immediate = false ) {
		const shouldCloseImmediately = immediate || ! this.isMobileFilterView();

		if ( shouldCloseImmediately ) {
			itemEl.classList.remove( 'is-open', 'is-active' );
			return;
		}

		itemEl.classList.remove( 'is-open' );
		window.setTimeout( () => {
			if ( ! itemEl.classList.contains( 'is-open' ) ) {
				itemEl.classList.remove( 'is-active' );
			}
		}, DySearchFilterController.desktopCloseDelay );
	}

	openItem( itemEl ) {
		itemEl.classList.add( 'is-active' );
		window.setTimeout( () => {
			if ( itemEl.classList.contains( 'is-active' ) ) {
				itemEl.classList.add( 'is-open' );
			}
		}, 1 );
	}

	closeAllItems( immediate = false ) {
		this.formEl.querySelectorAll( '.mdl_filter__item.is-active, .mdl_filter__item.is-open' ).forEach( ( itemEl ) => {
			this.closeItem( itemEl, immediate );
		} );
	}

	restoreDirectCategoryState() {
		let didRestore = false;

		this.formEl.querySelectorAll( '.mdl_filter__item[data-identifier="categories_no_tmp"]' ).forEach( ( itemEl ) => {
			if ( itemEl.dataset.openedFromOverview === 'true' ) {
				return;
			}

			if ( this.formEl.dataset.resetCategory !== 'true' && ! this.formEl.dataset.categoryFilters && ! itemEl.dataset.pendingCategoryLabel ) {
				return;
			}

			delete this.formEl.dataset.resetCategory;
			delete this.formEl.dataset.categoryFilters;
			this.clearPendingCategoryState( itemEl );
			didRestore = true;
		} );

		if ( didRestore ) {
			this.updateControls();
		}
	}

	openOverview() {
		const overviewEl = this.formEl.querySelector( '.mdl_filter__overview' );
		if ( ! overviewEl ) {
			return;
		}

		overviewEl.classList.add( 'is-active' );
		window.setTimeout( () => {
			if ( overviewEl.classList.contains( 'is-active' ) ) {
				overviewEl.classList.add( 'is-open' );
			}
		}, 1 );
	}

	closeOverview( immediate = false ) {
		const overviewEl = this.formEl.querySelector( '.mdl_filter__overview' );
		if ( ! overviewEl ) {
			return;
		}

		if ( immediate ) {
			overviewEl.classList.remove( 'is-open', 'is-active' );
			return;
		}

		overviewEl.classList.remove( 'is-open' );
		window.setTimeout( () => {
			if ( ! overviewEl.classList.contains( 'is-open' ) ) {
				overviewEl.classList.remove( 'is-active' );
			}
		}, DySearchFilterController.desktopCloseDelay );
	}

	isOverviewOpen() {
		return this.formEl.querySelector( '.mdl_filter__overview' )?.classList.contains( 'is-open' );
	}

	setOverviewLoading() {
		this.formEl.querySelector( '.mdl_filter__overview' )?.classList.add( 'is-loading' );
	}

	setFilterItemLoading( itemEl ) {
		itemEl?.classList.add( 'is-loading' );
	}

	setButtonLoading( buttonEl ) {
		buttonEl?.classList.add( 'en_button--loading' );
	}

	submitWithLoading( itemEl = null, buttonEl = null ) {
		this.setOverviewLoading();
		this.setFilterItemLoading( itemEl );
		this.setButtonLoading( buttonEl );
		window.requestAnimationFrame( () => {
			this.formEl.submit();
		} );
	}

	toggleOverview() {
		const overviewEl = this.formEl.querySelector( '.mdl_filter__overview' );
		if ( ! overviewEl ) {
			return;
		}

		if ( overviewEl.classList.contains( 'is-open' ) ) {
			this.resetFiltersToInitialState();
			this.closeOverview();
			return;
		}

		this.openOverview();
	}

	updateApplyButtonLabel( itemEl, fromOverview = false ) {
		const buttonEl = itemEl.querySelector( '.dy_filter_apply_button' );
		const textEl = buttonEl?.querySelector( '.mdl_filter__item-control-apply-text' );
		if ( ! buttonEl || ! textEl ) {
			return;
		}

		textEl.textContent = fromOverview
			? buttonEl.dataset.labelApply || textEl.textContent
			: buttonEl.dataset.labelSubmit || textEl.textContent;
	}

	openItemByIdentifier( identifier, fromOverview = false ) {
		if ( ! identifier ) {
			return;
		}

		const itemEl = this.formEl.querySelector( `.mdl_filter__item[data-identifier="${ CSS.escape( identifier ) }"]` );
		if ( ! itemEl ) {
			return;
		}

		const isOpen = itemEl.classList.contains( 'is-open' );
		this.closeAllItems( ! isOpen );

		if ( ! isOpen ) {
			itemEl.dataset.openedFromOverview = fromOverview ? 'true' : 'false';
			this.updateApplyButtonLabel( itemEl, fromOverview );
			this.openItem( itemEl );
		}
	}

	clearPendingCategoryState( itemEl ) {
		delete itemEl.dataset.pendingCategoryLabel;
		itemEl.classList.remove( 'has-pending-category' );
		itemEl.querySelectorAll( '.facet_menu__item.is-pending' ).forEach( ( categoryItemEl ) => {
			categoryItemEl.classList.remove( 'is-pending' );
		} );
	}

	getItemSelection( itemEl ) {
		if ( itemEl.dataset.identifier === SORT_FIELD_GROUP ) {
			const selectedInputEl = itemEl.querySelector( `.dy_filter_input[data-group="${ SORT_FIELD_GROUP }"]:checked` );
			const selectedLabelEl = selectedInputEl
				? itemEl.querySelector( `label[for="${ CSS.escape( selectedInputEl.id ) }"]` )
				: null;

			return {
				count: 0,
				selection: DySearchFilterController.getCleanLabel( selectedLabelEl ),
			};
		}

		if ( itemEl.dataset.identifier === 'categories_no_tmp' ) {
			if ( this.formEl.dataset.resetCategory === 'true' ) {
				return { count: 0, selection: '' };
			}

			if ( itemEl.dataset.pendingCategoryLabel ) {
				return {
					count: 1,
					selection: itemEl.dataset.pendingCategoryLabel,
				};
			}

			const currentCategoryEl = itemEl.querySelector( '.facet_menu__item--current .en-item-title, .facet_menu__item--current > .facet_menu__item_name' );
			const categoryLabel = DySearchFilterController.getCleanLabel( currentCategoryEl );

			return {
				count: categoryLabel ? 1 : 0,
				selection: categoryLabel,
			};
		}

		const selectedLabels = Array.from( itemEl.querySelectorAll( '.dy_filter_input:checked' ) )
			.map( ( inputEl ) => DySearchFilterController.getCleanLabel( itemEl.querySelector( `label[for="${ CSS.escape( inputEl.id ) }"]` ) ) )
			.filter( Boolean );
		const priceSliderEl = itemEl.querySelector( '.mdl_price_slider' );

		if ( priceSliderEl ) {
			const fromRange = priceSliderEl.querySelectorAll( 'input[type="range"]' )[ 0 ];
			const toRange = priceSliderEl.querySelectorAll( 'input[type="range"]' )[ 1 ];

			if ( fromRange && toRange && ( fromRange.value !== fromRange.dataset.default || toRange.value !== toRange.dataset.default ) ) {
				const fromText = priceSliderEl.querySelector( '.slider-inputs [data-price-from]' )?.value || '';
				const toText = priceSliderEl.querySelector( '.slider-inputs [data-price-to]' )?.value || '';
				selectedLabels.push( `${ fromText } - ${ toText }` );
			}
		}

		return {
			count: selectedLabels.length,
			selection: selectedLabels.join( ', ' ),
		};
	}

	updateOverviewFilters() {
		this.formEl.querySelectorAll( '.mdl_filter__overview-filter-item' ).forEach( ( overviewItemEl ) => {
			const identifier = overviewItemEl.dataset.identifier || '';
			const itemEl = this.formEl.querySelector( `.mdl_filter__item[data-identifier="${ CSS.escape( identifier ) }"]` );
			if ( ! itemEl ) {
				return;
			}

			const itemSelection = this.getItemSelection( itemEl );
			const titleContainerEl = overviewItemEl.querySelector( '.mdl_filter__overview-filter-item-title-container' );
			let countEl = overviewItemEl.querySelector( '.mdl_filter__overview-filter-item-count' );
			const selectionEl = overviewItemEl.querySelector( '.mdl_filter__overview-filter-item-selection' );

			overviewItemEl.classList.toggle( 'is-selected', itemSelection.count > 0 );

			if ( itemSelection.count > 0 ) {
				if ( ! countEl && titleContainerEl ) {
					countEl = document.createElement( 'span' );
					countEl.className = 'mdl_filter__overview-filter-item-count';
					titleContainerEl.appendChild( countEl );
				}

				if ( countEl ) {
					countEl.textContent = String( itemSelection.count );
				}
			} else if ( countEl ) {
				countEl.remove();
			}

			if ( selectionEl ) {
				selectionEl.textContent = itemSelection.selection;
			}
		} );
	}

	updateItemControls( itemEl ) {
		const isCategoryItem = itemEl.dataset.identifier === 'categories_no_tmp';
		const isSortItem = itemEl.dataset.identifier === SORT_FIELD_GROUP;
		const hasPendingCategorySelection = isCategoryItem && Object.keys( this.getPendingCategoryFilters() ).length > 0;
		const hasCategorySelection = isCategoryItem && this.formEl.dataset.resetCategory !== 'true' && ( hasPendingCategorySelection || this.hasInitialCategoryFilters() );
		const hasCategoryChanges = isCategoryItem && this.formEl.dataset.resetCategory === 'true' && this.hasInitialCategoryFilters();
		const hasPendingCategoryChanges = isCategoryItem && hasPendingCategorySelection;
		const inputs = Array.from( itemEl.querySelectorAll( '.dy_filter_input' ) );
		const selectableInputs = isSortItem
			? inputs.filter( ( inputEl ) => String( inputEl.dataset.group || '' ) !== SORT_FIELD_GROUP )
			: inputs;
		const hasSelection = selectableInputs.some( ( inputEl ) => {
			if ( inputEl.type === 'range' ) {
				return inputEl.value !== inputEl.dataset.default;
			}

			return inputEl.checked;
		} ) || hasCategorySelection;
		const hasChanges = inputs.some( ( inputEl ) => {
			if ( inputEl.type === 'range' ) {
				return inputEl.value !== inputEl.dataset.initial;
			}

			return inputEl.checked !== ( inputEl.dataset.initial === 'true' );
		} ) || hasCategoryChanges || hasPendingCategoryChanges;
		const applyButton = itemEl.querySelector( '.dy_filter_apply_button' );
		const resetButtons = itemEl.querySelectorAll( '.dy_filter_reset_button' );

		if ( applyButton ) {
			applyButton.toggleAttribute( 'disabled', ! hasChanges );
		}

		resetButtons.forEach( ( resetButton ) => {
			if ( resetButton.classList.contains( 'js_filter__item-mobile-reset' ) ) {
				resetButton.classList.toggle( 'en_hidden', ! hasSelection );
			}
			resetButton.toggleAttribute( 'disabled', ! hasSelection );
		} );

		itemEl.classList.toggle( 'is-selected', hasSelection );
	}

	isMobileFilterView() {
		const mobileHeadingEl = this.formEl.querySelector( '.mdl_filter__heading-mobile' );
		return mobileHeadingEl && window.getComputedStyle( mobileHeadingEl ).display !== 'none';
	}

	updateOverviewSubmitLabel( hasChanges ) {
		const submitButton = this.formEl.querySelector( '.mdl_filter__overview-submit' );
		if ( ! submitButton ) {
			return;
		}

		const saveLabel = submitButton.dataset.labelSave || submitButton.textContent;
		const showLabel = submitButton.dataset.labelShowNoCount || submitButton.dataset.labelShow || '';

		if ( ! this.isMobileFilterView() || ! showLabel ) {
			submitButton.textContent = saveLabel;
			return;
		}

		submitButton.textContent = showLabel.replace( /\s*%s\s*/u, ' ' ).trim();
	}

	updateControls() {
		this.syncNewArrivedExclusivity();
		const initialFilters = this.getComparableInitialFilters();
		const currentFilters = this.collectFilters();
		const payloadFilters = DySearchFilterController.getFilterPayload( currentFilters );
		const hasSelection = Object.keys( payloadFilters ).length > 0;
		const hasChanges = ! filtersEqual( initialFilters, currentFilters );
		const payloadInput = this.formEl.querySelector( '[data-dy-filter-payload]' );
		const submitButton = this.formEl.querySelector( '.mdl_filter__overview-submit' );
		const resetButton = this.formEl.querySelector( '.mdl_filter__overview-reset' );

		if ( payloadInput ) {
			payloadInput.value = encodeFilters( payloadFilters );
			payloadInput.disabled = payloadInput.value === '';
		}
		this.syncSortFieldInput( currentFilters );

		if ( submitButton ) {
			submitButton.toggleAttribute( 'disabled', ! hasChanges );
		}
		this.updateOverviewSubmitLabel( hasChanges );

		if ( resetButton ) {
			resetButton.classList.toggle( 'en_hidden', ! hasSelection );
			resetButton.toggleAttribute( 'disabled', ! hasSelection );
		}

		this.formEl.querySelectorAll( '.mdl_filter__item' ).forEach( ( itemEl ) => {
			this.updateItemControls( itemEl );
		} );
		this.updateOverviewFilters();
	}

	filterChoicesBySearch( inputEl ) {
		const query = String( inputEl.value || '' ).trim().toLowerCase();
		const itemEl = inputEl.closest( '.mdl_filter__item' );

		itemEl.querySelectorAll( '.mdl_filter__field-choice' ).forEach( ( choiceEl ) => {
			const label = String( choiceEl.dataset.filterLabel || '' );
			choiceEl.classList.toggle( 'en_hidden', query !== '' && ! label.includes( query ) );
		} );
	}

	resetPriceSlider( sliderEl ) {
		const rangeInputs = sliderEl.querySelectorAll( 'input[type="range"]' );
		rangeInputs.forEach( ( inputEl ) => {
			inputEl.value = inputEl.dataset.default || inputEl.value;
		} );

		DySearchFilterController.syncPriceSliderAfterProgrammaticChange( sliderEl );
	}

	resetPriceSliderToInitial( sliderEl ) {
		const rangeInputs = sliderEl.querySelectorAll( 'input[type="range"]' );
		rangeInputs.forEach( ( inputEl ) => {
			inputEl.value = inputEl.dataset.initial || inputEl.dataset.default || inputEl.value;
		} );

		DySearchFilterController.syncPriceSliderAfterProgrammaticChange( sliderEl );
	}

	resetSortToDefault( scopeEl ) {
		scopeEl.querySelectorAll( `.dy_filter_input[type="radio"][data-group="${ SORT_FIELD_GROUP }"]` ).forEach( ( inputEl ) => {
			inputEl.checked = inputEl.value === DEFAULT_SORT_FIELD;
		} );
	}

	resetFiltersToInitialState() {
		delete this.formEl.dataset.resetCategory;
		delete this.formEl.dataset.categoryFilters;
		this.formEl.querySelectorAll( '.mdl_filter__item[data-identifier="categories_no_tmp"]' ).forEach( ( itemEl ) => {
			this.clearPendingCategoryState( itemEl );
		} );
		this.formEl.querySelectorAll( '.dy_filter_input' ).forEach( ( inputEl ) => {
			if ( inputEl.type === 'range' ) {
				inputEl.value = inputEl.dataset.initial || inputEl.dataset.default || inputEl.value;
				return;
			}

			inputEl.checked = inputEl.dataset.initial === 'true';
		} );
		this.formEl.querySelectorAll( '.mdl_price_slider' ).forEach( ( sliderEl ) => {
			this.resetPriceSliderToInitial( sliderEl );
		} );
		this.updateControls();
	}

	resetCategoryFilter( itemEl = null ) {
		this.formEl.dataset.resetCategory = 'true';
		delete this.formEl.dataset.categoryFilters;

		if ( itemEl ) {
			this.clearPendingCategoryState( itemEl );
		} else {
			this.formEl.querySelectorAll( '.mdl_filter__item[data-identifier="categories_no_tmp"]' ).forEach( ( categoryItemEl ) => {
				this.clearPendingCategoryState( categoryItemEl );
			} );
		}
	}

	resetFilterItem( itemEl ) {
		if ( ! itemEl ) {
			return;
		}

		if ( itemEl.dataset.identifier === 'categories_no_tmp' ) {
			this.resetCategoryFilter( itemEl );
		}

		if ( itemEl.dataset.identifier === SORT_FIELD_GROUP ) {
			this.resetSortToDefault( itemEl );
			this.updateControls();
			return;
		}

		itemEl.querySelectorAll( '.dy_filter_input' ).forEach( ( inputEl ) => {
			inputEl.checked = false;
		} );
		itemEl.querySelectorAll( '.mdl_price_slider' ).forEach( ( sliderEl ) => {
			this.resetPriceSlider( sliderEl );
		} );
		this.updateControls();
	}

	init() {
		this.updateControls();

		this.formEl.addEventListener( 'click', ( event ) => {
			const target = getEventTargetElement( event );
			if ( ! target ) {
				return;
			}

			const itemTriggerEl = target.closest( '.mdl_filter__item-trigger' );

			if ( itemTriggerEl && this.formEl.contains( itemTriggerEl ) ) {
				const itemEl = itemTriggerEl.closest( '.mdl_filter__item' );
				const isOpen = itemEl.classList.contains( 'is-open' );

				this.closeAllItems( ! isOpen );
				if ( ! isOpen ) {
					itemEl.dataset.openedFromOverview = 'false';
					this.updateApplyButtonLabel( itemEl, false );
					this.openItem( itemEl );
				}
				return;
			}

			const overviewFilterTriggerEl = target.closest( '.js_filter__overview-filter-item-trigger' );
			if ( overviewFilterTriggerEl && this.formEl.contains( overviewFilterTriggerEl ) ) {
				const overviewItemEl = overviewFilterTriggerEl.closest( '.mdl_filter__overview-filter-item' );
				this.openItemByIdentifier( overviewItemEl?.dataset.identifier || '', true );
				return;
			}

			const mobileCloseEl = target.closest( '.js_filter__item-mobile-close' );
			if ( mobileCloseEl && this.formEl.contains( mobileCloseEl ) ) {
				this.restoreDirectCategoryState();
				this.closeAllItems();
				return;
			}

			const overviewCloseEl = target.closest( '.js_filter__overview-close' );
			if ( overviewCloseEl && this.formEl.contains( overviewCloseEl ) ) {
				event.preventDefault();
				this.resetFiltersToInitialState();
				this.closeOverview();
				return;
			}

			const overviewResetEl = target.closest( '.mdl_filter__overview-reset' );
			if ( overviewResetEl && this.formEl.contains( overviewResetEl ) ) {
				event.preventDefault();

				this.formEl.querySelectorAll( '.dy_filter_input' ).forEach( ( inputEl ) => {
					inputEl.checked = false;
				} );
				this.resetSortToDefault( this.formEl );
				this.formEl.querySelectorAll( '.mdl_price_slider' ).forEach( ( sliderEl ) => {
					this.resetPriceSlider( sliderEl );
				} );
				this.resetCategoryFilter();
				this.updateControls();
				return;
			}

			const overviewSubmitEl = target.closest( '.mdl_filter__overview-submit' );
			if ( overviewSubmitEl && this.formEl.contains( overviewSubmitEl ) ) {
				event.preventDefault();
				this.updateControls();
				this.submitWithLoading( null, overviewSubmitEl );
				return;
			}

			const resetButtonEl = target.closest( '.dy_filter_reset_button' );
			if ( resetButtonEl && this.formEl.contains( resetButtonEl ) ) {
				event.preventDefault();

				const itemEl = resetButtonEl.closest( '.mdl_filter__item' );
				this.resetFilterItem( itemEl );
				return;
			}

			const categoryLinkEl = target.closest( '.mdl_filter__item[data-identifier="categories_no_tmp"] .facet_menu__item_name' );
			if ( categoryLinkEl && this.formEl.contains( categoryLinkEl ) ) {
				const itemEl = categoryLinkEl.closest( '.mdl_filter__item' );
				if ( ! this.isMobileFilterView() || itemEl?.dataset.openedFromOverview !== 'true' ) {
					return;
				}

				event.preventDefault();
				categoryLinkEl.blur();

				const url = new URL( categoryLinkEl.href, window.location.href );
				const filters = decodeFilters( url.searchParams.get( 'filters' ) || '' );
				const categoryFilters = {};

				if ( filters.categories ) {
					categoryFilters.categories = filters.categories;
				}

				if ( filters.categories_no_tmp ) {
					categoryFilters.categories_no_tmp = filters.categories_no_tmp;
				}

				this.formEl.dataset.categoryFilters = JSON.stringify( categoryFilters );
				delete this.formEl.dataset.resetCategory;
				this.clearPendingCategoryState( itemEl );
				itemEl.classList.add( 'has-pending-category' );
				categoryLinkEl.closest( '.facet_menu__item' )?.classList.add( 'is-pending' );
				itemEl.dataset.pendingCategoryLabel = DySearchFilterController.getCleanLabel( categoryLinkEl );
				this.updateControls();
				return;
			}

			const applyButtonEl = target.closest( '.dy_filter_apply_button' );
			if ( applyButtonEl && this.formEl.contains( applyButtonEl ) ) {
				event.preventDefault();
				this.updateControls();
				const itemEl = applyButtonEl.closest( '.mdl_filter__item' );

				if ( this.isMobileFilterView() && itemEl?.dataset.openedFromOverview === 'true' ) {
					this.closeItem( itemEl );
					return;
				}

				this.submitWithLoading( itemEl, applyButtonEl );
				return;
			}

			const clickedTrigger = target.closest( '.mdl_filter__item-trigger' );
			const clickedContainer = target.closest( '.mdl_filter__item-container' );
			const clickedItem = target.closest( '.mdl_filter__item' );
			const clickedOverviewTrigger = target.closest( '.js_filter__overview-filter-item-trigger' );
			const clickedOverviewInner = target.closest( '.mdl_filter__overview-inner' );

			if ( clickedTrigger || clickedContainer || clickedOverviewTrigger || clickedOverviewInner ) {
				return;
			}

			if ( clickedItem === null ) {
				this.restoreDirectCategoryState();
				this.closeAllItems( true );
				if ( this.isOverviewOpen() && clickedOverviewInner === null ) {
					this.resetFiltersToInitialState();
				}
				this.closeOverview( true );
				return;
			}

			const openItem = this.formEl.querySelector( '.mdl_filter__item.is-open' );
			if ( openItem && openItem !== clickedItem ) {
				this.restoreDirectCategoryState();
				this.closeAllItems();
			}
		} );

		document.addEventListener( 'click', ( event ) => {
			const target = getEventTargetElement( event );
			if ( ! target ) {
				return;
			}

			const overviewTriggerEl = target.closest( '.js_filter__item-trigger-all-filter' );

			if ( overviewTriggerEl ) {
				event.preventDefault();
				this.toggleOverview();
				return;
			}

			const categoryResetEl = target.closest( '.dy_filter_category_reset_button' );
			if ( categoryResetEl ) {
				event.preventDefault();

				const targetFormEl = document.querySelector( '.dy_search_filter_form--dy-search' ) || this.formEl;
				const targetController = targetFormEl.DY_SEARCH_FILTER_CONTROLLER || this;
				targetController.resetCategoryFilter();
				targetController.updateControls();
				targetController.submitWithLoading( null, categoryResetEl );
				return;
			}

			if ( ! this.formEl.contains( target ) ) {
				this.restoreDirectCategoryState();
				this.closeAllItems( true );
				if ( this.isOverviewOpen() ) {
					this.resetFiltersToInitialState();
				}
				this.closeOverview( true );
			}
		} );

		this.formEl.addEventListener( 'change', ( event ) => {
			const target = getEventTargetElement( event );
			if ( ! target ) {
				return;
			}

			if ( target.matches( '.dy_filter_input' ) ) {
				if ( target.type === 'radio' && target.checked ) {
					const group = String( target.dataset.group || '' );
					if ( group ) {
						this.formEl.querySelectorAll( `.dy_filter_input[type="radio"][data-group="${ CSS.escape( group ) }"]` ).forEach( ( inputEl ) => {
							if ( inputEl !== target ) {
								inputEl.checked = false;
							}
						} );
					}
				}

				this.syncNewArrivedExclusivity( target );
				this.updateControls();
				return;
			}

			if ( target.matches( '.mdl_price_slider .slider-inputs input' ) ) {
				this.updateControls();
			}
		} );

		this.formEl.addEventListener( 'input', ( event ) => {
			const target = getEventTargetElement( event );
			if ( ! target ) {
				return;
			}

			if ( target.matches( '.mdl_filter__item-search-input' ) ) {
				this.filterChoicesBySearch( target );
			}
		} );

		this.formEl.addEventListener( 'submit', () => {
			const url = new URL( window.location.href );
			url.searchParams.delete( 'page' );
			this.formEl.action = url.pathname;
		} );
	}
}

document.addEventListener( 'DOMContentLoaded', () => {
	document.querySelectorAll( '.dy_search_filter_form' ).forEach( ( formEl ) => {
		formEl.DY_SEARCH_FILTER_CONTROLLER ??= new DySearchFilterController( formEl );
	} );
} );
